
import React, { useState, useRef } from 'react';
import { Quadrant, Task, Category } from '../types';
import { nudgeService } from '../services/nudgeService';
import {
  Plus, CheckCircle2, Circle, Archive, MoreHorizontal, Calendar, Zap, ListChecks,
  MoveRight, AlertTriangle, Lock
} from 'lucide-react';

const QUADRANT_UI_CONFIG = {
  [Quadrant.DO]: {
    label: 'Do First',
    sublabel: 'Urgent & Important',
    accent: 'bg-rose-500',
    rail: 'border-l-rose-500',
    border: 'border-rose-200 dark:border-rose-900/50',
    text: 'text-rose-600',
    bg: 'bg-gradient-to-br from-white to-rose-50/80 dark:from-slate-900 dark:to-rose-900/20'
  },
  [Quadrant.SCHEDULE]: {
    label: 'Schedule',
    sublabel: 'Important, Not Urgent',
    accent: 'bg-blue-500',
    rail: 'border-l-blue-500',
    border: 'border-blue-200 dark:border-blue-900/50',
    text: 'text-blue-600',
    bg: 'bg-gradient-to-br from-white to-blue-50/80 dark:from-slate-900 dark:to-blue-900/20'
  },
  [Quadrant.DELEGATE]: {
    label: 'Delegate',
    sublabel: 'Urgent, Not Important',
    accent: 'bg-amber-500',
    rail: 'border-l-amber-500',
    border: 'border-amber-200 dark:border-amber-900/50',
    text: 'text-amber-600',
    bg: 'bg-gradient-to-br from-white to-amber-50/80 dark:from-slate-900 dark:to-amber-900/20'
  },
  [Quadrant.ELIMINATE]: {
    label: 'Postpone',
    sublabel: 'Neither',
    accent: 'bg-slate-400',
    rail: 'border-l-slate-400',
    border: 'border-slate-200 dark:border-slate-800',
    text: 'text-slate-500',
    bg: 'bg-gradient-to-br from-white to-slate-100/80 dark:from-slate-900 dark:to-slate-800/30'
  }
};

interface MatrixProps {
  tasks: Task[];
  categories: Category[];
  onAddTask: (q: Quadrant) => void;
  onToggleTask: (id: string) => void;
  onArchiveTask: (id: string) => void;
  onSelectTask: (task: Task) => void;
  onMoveTask?: (id: string, q: Quadrant) => void;
  isPremium: boolean;
  onUpgradeRequest?: (feature: string) => void;
}

const PREMIUM_QUADRANTS = [Quadrant.DELEGATE, Quadrant.ELIMINATE];

export const EisenhowerMatrix: React.FC<MatrixProps> = ({
  tasks,
  categories,
  onAddTask,
  onToggleTask,
  onArchiveTask,
  onSelectTask,
  onMoveTask,
  isPremium,
  onUpgradeRequest
}) => {
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  
  // Touch Drag State
  const [touchDragTask, setTouchDragTask] = useState<Task | null>(null);
  const [touchPos, setTouchPos] = useState({ x: 0, y: 0 });
  const longPressTimer = useRef<any>(null);

  // --- Desktop DnD ---
  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggingId(task.id);
    e.dataTransfer.setData('text/plain', task.id);
    e.dataTransfer.effectAllowed = 'move';
    // Reset touch state just in case
    setTouchDragTask(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, quadrant: Quadrant) => {
    e.preventDefault();
    if (!isPremium && PREMIUM_QUADRANTS.includes(quadrant)) {
      onUpgradeRequest?.(QUADRANT_UI_CONFIG[quadrant].label + ' Quadrant');
      setDraggingId(null);
      return;
    }
    const taskId = e.dataTransfer.getData('text/plain');
    if (taskId && onMoveTask) {
      onMoveTask(taskId, quadrant);
    }
    setDraggingId(null);
  };

  // --- Mobile Touch DnD ---
  const handleTouchStart = (e: React.TouchEvent, task: Task) => {
    // Ignore if touching a button
    if ((e.target as HTMLElement).closest('button')) return;

    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    longPressTimer.current = setTimeout(() => {
      setDraggingId(task.id);
      setTouchDragTask(task);
      setTouchPos({ x: startX, y: startY });
      if (navigator.vibrate) navigator.vibrate(50);
    }, 500); // 500ms long press to activate drag
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (longPressTimer.current && !touchDragTask) {
        // If moving before activation, cancel the timer (scrolling intent)
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
    }

    if (touchDragTask) {
      e.preventDefault(); // Stop scrolling
      const touch = e.touches[0];
      setTouchPos({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
    }

    if (touchDragTask) {
        const touch = e.changedTouches[0];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const quadrantEl = target?.closest('[data-quadrant]');
        
        if (quadrantEl) {
            const q = quadrantEl.getAttribute('data-quadrant') as Quadrant;
            if (q && q !== touchDragTask.currentQuadrant) {
                if (!isPremium && PREMIUM_QUADRANTS.includes(q)) {
                    onUpgradeRequest?.(QUADRANT_UI_CONFIG[q].label + ' Quadrant');
                } else if (onMoveTask) {
                    onMoveTask(touchDragTask.id, q);
                }
            }
        }
        setTouchDragTask(null);
        setDraggingId(null);
    }
  };

  const renderTask = (task: Task) => {
    const category = categories.find(c => c.id === task.category);
    const isDragging = draggingId === task.id;
    const isStagnant = nudgeService.isTaskStagnant(task);
    const isMenuOpen = activeMenuId === task.id;
    
    return (
      <div 
        key={task.id}
        draggable={!task.isCompleted}
        onDragStart={(e) => handleDragStart(e, task)}
        onTouchStart={(e) => handleTouchStart(e, task)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => onSelectTask(task)}
        className={`group relative p-3 bg-white dark:bg-slate-800 rounded-xl border ${isStagnant ? 'border-amber-200 dark:border-amber-900/50 shadow-amber-500/10' : 'border-slate-100 dark:border-slate-700'} shadow-sm hover:shadow-md transition-all cursor-pointer ${task.isCompleted ? 'opacity-60' : ''} ${isDragging ? 'opacity-40 border-dashed border-slate-400' : ''}`}
        style={{ touchAction: 'pan-y' }} // Allow vertical scroll, overridden via JS when dragging
      >
        <div className="flex items-start gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleTask(task.id); }}
            className={`mt-0.5 transition-colors ${task.isCompleted ? 'text-emerald-500' : 'text-slate-300 hover:text-emerald-500'}`}
          >
            {task.isCompleted ? <CheckCircle2 size={18} /> : <Circle size={18} />}
          </button>
          
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium leading-snug mb-1.5 ${task.isCompleted ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
              {task.title}
            </p>
            
            <div className="flex flex-wrap items-center gap-2">
              {category && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-700">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: category.color }} />
                  <span className="text-[10px] font-bold text-slate-500 uppercase">{category.name}</span>
                </div>
              )}
              
              {task.dueDate && (
                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${task.dueDate < Date.now() && !task.isCompleted ? 'bg-rose-50 border-rose-100 text-rose-600' : 'bg-slate-50 border-slate-100 text-slate-500'}`}>
                  <Calendar size={10} />
                  <span className="text-[10px] font-bold">{new Date(task.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                </div>
              )}

              {task.hasSubtasks && (
                <div className="flex items-center gap-1 text-slate-400">
                  <ListChecks size={12} />
                  <span className="text-[10px] font-bold">{task.completedSubtaskCount}/{task.subtaskCount}</span>
                </div>
              )}

              {isStagnant && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-100 text-amber-600" title="Stagnant: No activity > 24h">
                  <AlertTriangle size={10} />
                  <span className="text-[10px] font-bold">Stagnant</span>
                </div>
              )}
            </div>
          </div>

          <button 
            onClick={(e) => { e.stopPropagation(); setActiveMenuId(isMenuOpen ? null : task.id); }}
            className={`p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shrink-0 ${isMenuOpen ? 'opacity-100 bg-slate-100 dark:bg-slate-700' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}
            aria-label="Task Options"
          >
            <MoreHorizontal size={18} />
          </button>

          {activeMenuId === task.id && (
            <div className="absolute right-0 top-8 z-20 w-48 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 p-1 animate-in zoom-in-95 duration-100">
              <div className="text-[10px] font-bold text-slate-400 uppercase px-3 py-2">Move To</div>
              {Object.values(Quadrant).filter(q => q !== task.currentQuadrant).map(q => {
                const isLocked = !isPremium && PREMIUM_QUADRANTS.includes(q);
                return (
                  <button
                    key={q}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLocked) {
                        onUpgradeRequest?.(QUADRANT_UI_CONFIG[q].label + ' Quadrant');
                      } else {
                        onMoveTask?.(task.id, q);
                      }
                      setActiveMenuId(null);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs font-medium rounded-lg flex items-center gap-2 ${isLocked ? 'text-slate-400 hover:bg-slate-50' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                  >
                    {isLocked ? <Lock size={12} /> : <MoveRight size={12} />}
                    {QUADRANT_UI_CONFIG[q].label}
                    {isLocked && <span className="ml-auto text-[9px] font-bold text-blue-500">PRO</span>}
                  </button>
                );
              })}
              <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
              <button
                onClick={(e) => { e.stopPropagation(); onArchiveTask(task.id); setActiveMenuId(null); }}
                className="w-full text-left px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg flex items-center gap-2"
              >
                <Archive size={12} />
                Archive
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderQuadrant = (quadrant: Quadrant) => {
    const config = QUADRANT_UI_CONFIG[quadrant];
    const qTasks = tasks.filter(t => t.currentQuadrant === quadrant && t.status !== 'ARCHIVED' && !t.isCompleted);
    const isLocked = !isPremium && PREMIUM_QUADRANTS.includes(quadrant);

    return (
      <div
        className={`flex flex-col w-full rounded-[2rem] border overflow-hidden ${config.bg} ${config.border} relative h-full min-h-[24rem] max-h-[40rem] shadow-sm transition-all duration-300`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, quadrant)}
        data-quadrant={quadrant}
      >
        <div className={`px-6 py-4 border-b bg-white/50 backdrop-blur-sm flex justify-between items-center ${config.border} shrink-0`}>
          <div>
            <h3 className={`text-sm font-black uppercase tracking-widest ${config.text}`}>
              {config.label}
              {isLocked && <Lock size={12} className="inline ml-2 -mt-0.5 text-slate-400" />}
            </h3>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">{config.sublabel}</p>
          </div>
          {!isLocked && (
            <button
              onClick={() => onAddTask(quadrant)}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all shadow-sm active:scale-95 ${config.accent} text-white hover:opacity-90`}
            >
              <Plus size={18} />
            </button>
          )}
        </div>

        <div className="flex-1 p-4 overflow-y-auto custom-scrollbar space-y-3 relative">
          {isLocked ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer z-10"
              onClick={() => onUpgradeRequest?.(config.label + ' Quadrant')}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-slate-200 shadow-lg text-center space-y-3 max-w-[240px]">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto">
                  <Lock size={22} />
                </div>
                <p className="text-sm font-bold text-slate-800">Premium Feature</p>
                <p className="text-[11px] text-slate-500 leading-relaxed">Upgrade to unlock the {config.label} quadrant and full Eisenhower Matrix.</p>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                  <Zap size={12} /> Upgrade to Unlock
                </span>
              </div>
            </div>
          ) : qTasks.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-600 space-y-2 opacity-50 min-h-[200px]">
              <div className={`p-3 rounded-full bg-slate-100 dark:bg-slate-800`}>
                <Zap size={20} />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest">Sector Clear</p>
            </div>
          ) : (
            qTasks.map(renderTask)
          )}
        </div>

        {/* Drop Zone Highlight for Desktop */}
        {draggingId && (
           <div className="absolute inset-0 bg-blue-500/5 pointer-events-none border-2 border-blue-500/20 rounded-[2rem] animate-pulse" style={{ display: 'none' }} />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-24">
        {renderQuadrant(Quadrant.DO)}
        {renderQuadrant(Quadrant.SCHEDULE)}
        {renderQuadrant(Quadrant.DELEGATE)}
        {renderQuadrant(Quadrant.ELIMINATE)}
        
        {activeMenuId && (
          <div 
            className="fixed inset-0 z-10 bg-transparent" 
            onClick={() => setActiveMenuId(null)} 
          />
        )}
      </div>

      {/* Mobile Drag Ghost Element */}
      {touchDragTask && (
        <div 
          className="fixed z-[100] p-4 bg-white rounded-xl shadow-2xl border-2 border-blue-500 pointer-events-none w-64 transform -translate-x-1/2 -translate-y-1/2 opacity-90"
          style={{ left: touchPos.x, top: touchPos.y }}
        >
          <p className="text-sm font-bold text-slate-900 line-clamp-1">{touchDragTask.title}</p>
          <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest mt-1">Moving...</p>
        </div>
      )}
    </>
  );
};
